/**
 * @ Author: Housefolios
 * @ Create Time: 2021-11-22 08:26:27
 * @ Modified by: David Helmick
 * @ Modified time: 2025-07-29 16:33:59
 * @ Description: Top level component. Sets up the cache and graphql link.
 */

import React from 'react'
import ReactDOM from 'react-dom'
import {
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  Observable,
  CombinedGraphQLErrors,
  ServerError,
} from '@apollo/client'
import { ApolloProvider } from '@apollo/client/react'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { createClient } from 'graphql-ws'
import { getMainDefinition } from '@apollo/client/utilities'
import { ErrorLink } from '@apollo/client/link/error'
import UploadHttpLink from 'apollo-upload-client/UploadHttpLink.mjs'
import { includes } from './utils/lodash'
import App from './App'
import * as serviceWorker from './serviceWorker'
import { SnackbarProvider } from 'notistack'
import LeaveTeamNotice from '@/housefolios-components/Team/LeaveTeamNotice'
import { signOut } from '@/housefolios-components/SignOut'
import { saveAuthToken } from './utils/auth'
import { installUpdateDepthDiagnostics } from './utils/updateDepthDiagnostics'

installUpdateDepthDiagnostics()
import { createRoot } from 'react-dom/client'

// Show full Apollo error messages in dev (prevents redirect-to-docs behavior).
// Safe for production because it is gated behind Vite's dev flag.
if (import.meta.env.DEV) {
  const { loadDevMessages, loadErrorMessages } = await import(
    '@apollo/client/dev'
  )
  loadDevMessages()
  loadErrorMessages()
}

const graphqlHttpUri = import.meta.env.VITE_URI || '/graphql'
if (!import.meta.env.VITE_URI) {
  // This prevents a silent misconfiguration where Apollo can't actually send requests,
  // which can manifest as confusing runtime errors (e.g. "reading 'call'").
  console.warn(
    '[Apollo] VITE_URI is not set. Falling back to "/graphql". If your API is on a different host, set VITE_URI.',
  )
}

const httpLink = new UploadHttpLink({ uri: graphqlHttpUri  })

const graphqlWsUrl =
  import.meta.env.VITE_WSURI ||
  `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${
    window.location.host
  }/graphql/subscriptions`

if (!import.meta.env.VITE_WSURI) {
  console.warn(
    '[Apollo] VITE_WSURI is not set. Falling back to current host at "/graphql/subscriptions". If your WS server is elsewhere, set VITE_WSURI.',
  )
}

// Update the wsLink to include the authentication token
const wsLink = new GraphQLWsLink(
  createClient({
    url: graphqlWsUrl,
    connectionParams: () => ({
      authToken: localStorage.getItem('token'),
    }),
    lazy: true,
    retryAttempts: 50, // Try reconnecting up to 50 times
    shouldRetry: () => true, // Always retry on disconnect
    // Client pings keep the socket alive through Railway's idle timeout.
    keepAlive: 10_000,
    on: {
      closed: (event) => {
        console.warn('WebSocket closed', event)
      },
      connected: () => {
        console.info('WebSocket connected')
      },
      error: (error) => {
        console.error('WebSocket error', error)
      },
    },
  }),
)

const terminatingLink = ApolloLink.split(
  ({ query  }) => {
    const { kind, operation } = getMainDefinition(query)
    return (
      kind === 'OperationDefinition' && operation === 'subscription'
    )
  },
  wsLink,
  httpLink,
)

const authLink = new ApolloLink((operation, forward) => {
  // Capture last attempted operation for client error telemetry (avoid variables/PII).
  try {
    /** @type {any} */ (window).__hfLastApolloOperation = {
      name: operation.operationName || 'unknown',
      at: new Date().toISOString(),
    }
  } catch (_e) {
    // ignore
  }

  operation.setContext(({ headers = {} }) => {
    const token = localStorage.getItem('token')
    if (token) {
      headers = { ...headers, 'x-token': token }
    }

    const hostname = import.meta.env.VITE_HOSTNAME
    let domain
    if (
      hostname === 'localhost' ||
      hostname === 'dev-admin.housefolios.com'
    ) {
      const urlParams = new URLSearchParams(window.location.search)
      domain = urlParams.get('domain')
    } else {
      domain = window.location.hostname
    }

    if (domain) {
      headers = { ...headers, domain: domain }
    }

    return { headers }
  })

  const observable = typeof forward === 'function' ? forward(operation) : null
  if (!observable || typeof observable.subscribe !== 'function') {
    // If we can't forward, no network request will be sent.
    // Throwing here gives a much clearer signal than an internal ".call" crash.
    const opName = operation.operationName || 'unknown'
    console.error('[Apollo] Link forwarding failed (no terminating link?)', {
      operationName: opName,
      graphqlHttpUri,
      context: operation.getContext?.(),
    })
    throw new Error(
      `GraphQL client is not configured correctly (cannot forward operation "${opName}"). Check VITE_URI.`,
    )
  }

  // Capture refresh tokens from response headers without re-wrapping the
  // Observable (AC4 uses rxjs; a custom wrapper can leave queries stuck loading).
  return new Observable((observer) => {
    const subscription = observable.subscribe({
      next: (data) => {
        try {
          const context = operation.getContext()
          const headers = context?.response?.headers
          if (headers) {
            const refreshToken = headers.get('refreshToken')
            if (refreshToken) {
              saveAuthToken(refreshToken)
            }
          }
        } catch (error) {
          console.log('authLinkResponseError', error)
        }

        observer.next(data)
      },
      error: (err) => observer.error(err),
      complete: () => observer.complete(),
    })

    return () => {
      if (subscription && typeof subscription.unsubscribe === 'function') {
        subscription.unsubscribe()
      }
    }
  })
})

const errorLink = new ErrorLink(({ error, operation  }) => {
  if (CombinedGraphQLErrors.is(error)) {
    error.errors.forEach(({ message  }) => {
      const expired = includes(message, 'Your session expired')

      if (message === 'UNAUTHENTICATED' || expired) {
        signOut(client)
      }
    })
  } else if (ServerError.is(error)) {
    console.error('[Apollo] Network error', {
      operationName: operation?.operationName,
      statusCode: error.statusCode,
      message: error.message,
      result: error.result,
      variables: operation?.variables,
    })
    if (error.statusCode === 401) {
      signOut(client)
    }
  } else if (error) {
    console.error('[Apollo] Other error', {
      operationName: operation?.operationName,
      message: error.message,
    })
  }
})

const link = ApolloLink.from([authLink, errorLink, terminatingLink])

const cache = new InMemoryCache({
  typePolicies: {
    // Nested on Property without _id in most queries — embed + merge
    // instead of normalizing, to avoid Apollo cache write warnings.
    Acquisition: {
      keyFields: false,
    },
    Analysis: {
      keyFields: false,
    },
    Analytics: {
      keyFields: false,
    },
    Property: {
      fields: {
        acquisition: {
          merge: true,
        },
      },
    },
  },
})

const client = new ApolloClient({
  link,
  cache,
  // Apollo Client 4 defaults this to true, which keeps `loading` true during
  // refetch/fetchMore and can unmount session-gated trees on background fetches.
  defaultOptions: {
    watchQuery: {
      notifyOnNetworkStatusChange: false,
    },
  },
})

const container = document.getElementById('root')
if (container) {
  // Clears the pre-React deploy-recovery watcher in index.html.
  container.dataset.hfBooted = '1'
}
const root = createRoot(container) // createRoot(container!) if you use TypeScript
root.render(
  <ApolloProvider client={client}>
    <SnackbarProvider maxSnack={3}>
      <LeaveTeamNotice />
      <App />
    </SnackbarProvider>
  </ApolloProvider>,
)

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
